/**
 * GameBoard Component
 * Reusable board grid renderer for both gameplay and replays
 * Canonical version extracted from Board.js
 */

import React from 'react';
import PropTypes from 'prop-types';
import { MferHead } from './MferHead';

function GameBoard({
  board,
  levelClass,
  levelID,
  imageMap,
  selectedJewel,
  shouldBeCleared,
  onJewelClick,
  barrelState = {},
  readOnly = false,
  showCoordinates = false,
  previewSwap = null
}) {
  return (
    <div className={`board ${levelClass}`} data-testid="game-board">
      {board.map((row, rowIndex) => (
        <div className="row" key={rowIndex}>
          {row.map((jewelType, colIndex) => {
            const isSelected = selectedJewel &&
              selectedJewel.rowIndex === rowIndex &&
              selectedJewel.colIndex === colIndex;

            const isClearedCell = shouldBeCleared &&
              shouldBeCleared.some(([r, c]) => r === rowIndex && c === colIndex);

            // Check if this position has a barrel
            const barrelKey = `${rowIndex},${colIndex}`;
            const barrelHealth = barrelState[barrelKey];

            // Check if this cell is part of the preview swap
            let previewInfo = null;
            if (previewSwap) {
              const [fromRow, fromCol] = previewSwap.from;
              const [toRow, toCol] = previewSwap.to;

              if (rowIndex === fromRow && colIndex === fromCol) {
                previewInfo = { isFrom: true, direction: previewSwap.direction };
              } else if (rowIndex === toRow && colIndex === toCol) {
                previewInfo = { isFrom: false, direction: previewSwap.direction };
              }
            }

            return (
              <MferHead
                key={colIndex}
                type={jewelType}
                x={rowIndex}
                y={colIndex}
                imageMap={imageMap}
                levelID={levelID}
                selectedJewel={isSelected ? { rowIndex, colIndex } : null}
                shouldBeCleared={isClearedCell}
                onClick={readOnly ? () => {} : () => onJewelClick(rowIndex, colIndex)}
                barrelHealth={barrelHealth}
                showCoordinates={showCoordinates}
                previewSwap={previewInfo}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

GameBoard.propTypes = {
  board: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
  levelClass: PropTypes.string.isRequired,
  levelID: PropTypes.string.isRequired,
  imageMap: PropTypes.object.isRequired,
  selectedJewel: PropTypes.shape({
    rowIndex: PropTypes.number,
    colIndex: PropTypes.number
  }),
  shouldBeCleared: PropTypes.array,
  onJewelClick: PropTypes.func,
  barrelState: PropTypes.object,
  readOnly: PropTypes.bool,
  showCoordinates: PropTypes.bool,
  previewSwap: PropTypes.shape({
    from: PropTypes.arrayOf(PropTypes.number),
    to: PropTypes.arrayOf(PropTypes.number),
    direction: PropTypes.oneOf(['up', 'down', 'left', 'right']),
    valid: PropTypes.bool
  })
};

GameBoard.defaultProps = {
  selectedJewel: null,
  shouldBeCleared: [],
  onJewelClick: () => {},
  barrelState: {},
  readOnly: false,
  showCoordinates: false,
  previewSwap: null
};

export default GameBoard;
